// (imports remain unchanged)
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import "./JobDetailPage.styled.css";
import ModalBox from "../components/ModalBox";
import CandidateTable from "../components/CandidateTable";
import TestConfigForm from "../components/TestConfig/TestConfigForm";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const defaultTestConfig = {
  technical: {
    enabled: true,
    language: "vi",
    difficulty: "job",
    question_count: 10,
  },
  iq: {
    enabled: false,
    language: "vi",
    question_count: 5,
  },
  english_grammar: {
    enabled: true,
    language: "en",
    english_level: "B2",
    question_count: 10,
  },
  english_communication: {
    enabled: true,
    language: "en",
    english_level: "B2",
    question_count: 10,
  },
  domain_knowledge: {
    enabled: false,
    language: "vi",
    question_count: 5,
  },
};

const getTestMaxScore = (testConfig) => {
  if (!testConfig) return null;

  let total = 0;
  Object.values(testConfig).forEach(section => {
    if (section?.enabled && Number.isInteger(section.question_count)) {
      total += section.question_count;
    }
  });

  return total;
};

function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobTitle, setJobTitle] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [sortBy, setSortBy] = useState("score");
  const [sortDir, setSortDir] = useState("desc");

  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);

  const [showJDModal, setShowJDModal] = useState(false);
  const [jdContent, setJdContent] = useState("");
  const [isUpdatingJD, setIsUpdatingJD] = useState(false);

  const [showTestConfigModal, setShowTestConfigModal] = useState(false);
  const [testConfig, setTestConfig] = useState(defaultTestConfig);
  const [draftTestConfig, setDraftTestConfig] = useState(null);
  const [testDurationMinutes, setTestDurationMinutes] = useState(60);

  const [showUploadJD, setShowUploadJD] = useState(false);
  const [showUploadCV, setShowUploadCV] = useState(false);
  const [jdFile, setJdFile] = useState(null);
  const [cvFile, setCvFile] = useState(null);

  const [showUnfocusModal, setShowUnfocusModal] = useState(false);
  const [unfocusData, setUnfocusData] = useState(null);

  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);

  const fetchCandidates = useCallback(async () => {
    setIsLoadingCandidates(true);
    try {
      const res = await api.get(`/candidates/${jobId}`);
      setCandidates(res.data);
    } catch {
      alert("Failed to load candidates");
    } finally {
      setIsLoadingCandidates(false);
    }
  }, [jobId]);

  const fetchJobDetail = useCallback(async () => {
    try {
      const res = await api.get(`/jobs/${jobId}`);
      const job = res.data;

      setJobTitle(job.title);
      if (job.test_duration_minutes !== null && job.test_duration_minutes !== undefined) {
        setTestDurationMinutes(job.test_duration_minutes);
      }

      if (job.test_config_json) {
        try {
          const parsed =
            typeof job.test_config_json === "string"
              ? JSON.parse(job.test_config_json)
              : job.test_config_json;

          setTestConfig(parsed);
        } catch (err) {
          console.error("Failed to parse test_config_json", err);
          setTestConfig(defaultTestConfig);
        }
      } else {
        setTestConfig(defaultTestConfig);
      }
    } catch {
      console.error("Failed to fetch job title");
    }
  }, [jobId]);

  const fetchJDContent = useCallback(async () => {
    try {
      const res = await api.get(`/jobs/${jobId}/jd`);
      setJdContent(res.data.jd_text);
    } catch {
      console.error("Failed to fetch JD content");
    }
  }, [jobId]);

  const handleUpdateJD = async () => {
    setIsUpdatingJD(true);
    try {
      await api.put(`/jobs/${jobId}/jd`, { jd_text: jdContent });
      setShowJDModal(false);
    } catch {
      alert("Failed to update JD");
    } finally {
      setIsUpdatingJD(false);
    }
  };

  const handleUploadJD = async () => {
    const formData = new FormData();
    formData.append("jd_file", jdFile);
    try {
      await api.post(`/jobs/${jobId}/jd`, formData);
      setShowUploadJD(false);
      fetchJDContent();
    } catch {
      alert("Failed to upload JD");
    }
  };

  const handleUploadCVs = async () => {
    const formData = new FormData();
    formData.append("job_id", jobId);
    formData.append("file", cvFile);
    try {
      await api.post(`/candidates/upload`, formData);
      setShowUploadCV(false);
      fetchCandidates();
    } catch {
      alert("Failed to upload CVs");
    }
  };

  const handleExport = () => {
    window.open(`${API_BASE_URL}/candidates/export/${jobId}`, "_blank");
  };

  const handleReEvaluate = async () => {
    if (selectedCandidateIds.length === 0) {
      alert("Please select candidate(s) to re-evaluate.");
      return;
    }
    try {
      await api.post(`/candidates/reevaluate`, {
        candidate_ids: selectedCandidateIds,
      });
      fetchCandidates();
    } catch {
      alert("Failed to re-evaluate candidates.");
    }
  };

  const handleDeleteCVs = async () => {
    if (selectedCandidateIds.length === 0) {
      alert("Please select candidate(s) to delete.");
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedCandidateIds.length} candidate(s)? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await api.delete(`/candidates`, {
        data: { candidate_ids: selectedCandidateIds },
      });
      fetchCandidates();
    } catch {
      alert("Failed to delete candidates.");
    }
  };

  const handleSaveTestConfig = async (configToSave) => {
    try {
      await api.put(`/jobs/${jobId}/test-config`, {
        test_config: configToSave,
        test_duration_minutes: testDurationMinutes,
      });
      setShowTestConfigModal(false);
      alert("Test configuration saved");
    } catch (err) {
      console.error(err);
      alert("Failed to save test configuration");
    }
  };

  const handleGenerateTest = async () => {
    if (selectedCandidateIds.length === 0) {
      alert("Please select candidate(s) to generate test.");
      return;
    }

    for (const id of selectedCandidateIds) {
      try {
        await api.post(`/candidates/${id}/generate-test`);

        // optimistic update từng candidate thành công
        setCandidates((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, test_status: "generating" }
              : c
          )
        );
      } catch (err) {
        console.error(`Failed to generate test for candidate ${id}`, err);
      }
    }
  };

  const handlePreviewTest = () => {
    const readyCandidates = candidates.filter(
      (c) =>
        selectedCandidateIds.includes(c.id) &&
        c.test_status === "ready"
    );

    if (readyCandidates.length === 0) {
      alert("No READY test to preview.");
      return;
    }

    readyCandidates.forEach((c) => {
      window.open(
        `/application-test/preview/${c.application_code}`,
        "_blank"
      );
    });
  };

  const handleCheckUnfocus = async () => {
    if (selectedCandidateIds.length !== 1) {
      alert("Please select exactly one candidate to check un-focus.");
      return;
    }

    const candidateId = selectedCandidateIds[0];

    try {
      const res = await api.get(`/candidates/${candidateId}/unfocus`);
      setUnfocusData(res.data);
      setShowUnfocusModal(true);
    } catch {
      alert("Failed to load un-focus information.");
    }
  };

  const sortedCandidates = [...candidates].sort((a, b) => {
    // Sort by Test Score (special handling)
    if (sortBy === "test_score") {
      const aCompleted = a.test_status === "completed";
      const bCompleted = b.test_status === "completed";

      // Completed always on top
      if (aCompleted !== bCompleted) {
        return aCompleted ? -1 : 1;
      }

      // Both completed → compare score
      if (aCompleted && bCompleted) {
        const scoreA = a.test_total_score ?? 0;
        const scoreB = b.test_total_score ?? 0;
        return sortDir === "asc"
          ? scoreA - scoreB
          : scoreB - scoreA;
      }

      // Both not completed → keep stable
      return 0;
    }

    // Default numeric sort
    const valA = a[sortBy];
    const valB = b[sortBy];

    if (typeof valA === "number" && typeof valB === "number") {
      return sortDir === "asc" ? valA - valB : valB - valA;
    }

    // String sort (e.g. name)
    return sortDir === "asc"
      ? String(valA ?? "").localeCompare(String(valB ?? ""))
      : String(valB ?? "").localeCompare(String(valA ?? ""));
  });

  useEffect(() => {
    fetchCandidates();
    fetchJobDetail();
    fetchJDContent();
  }, [fetchCandidates, fetchJobDetail, fetchJDContent]);

  return (
    <div className="job-detail">
      <div className="job-header">
        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
        <h2>Job #{jobId}: {jobTitle}</h2>
        <div className="button-group">
          <button onClick={() => setShowJDModal(true)}>View JD</button>
          <button onClick={() => setShowUploadJD(true)}>Upload/Replace JD</button>
          <button
            onClick={() => {
              setDraftTestConfig(JSON.parse(JSON.stringify(testConfig)));
              setShowTestConfigModal(true);
            }}
          >
            🧪 Test Config
          </button>
          <button onClick={handleExport}>📥 Export</button>
        </div>
      </div>

      <div className="sort-toolbar">
        <div className="sort-section">
          <label><strong>Sort by:</strong></label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="score">Matching Score</option>
            <option value="cv_ai_score">AI Score</option>
            <option value="test_score">Test Score</option>
            <option value="full_name">Name</option>
          </select>
          <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}>
            {sortDir === "asc" ? "⬆️" : "⬇️"}
          </button>
        </div>
        <div className="button-group">
          <button onClick={() => setShowUploadCV(true)}>Upload CV(s)</button>
          <button onClick={handleReEvaluate}>Re-evaluate</button>
          <button onClick={handleGenerateTest}>Generate test</button>
          <button onClick={handlePreviewTest}>Preview test</button>
          <button onClick={handleCheckUnfocus}>Check un-focus</button>
          <button onClick={handleDeleteCVs}>Delete</button>
        </div>
      </div>

      <CandidateTable
        sortedCandidates={sortedCandidates}
        onSelectionChange={setSelectedCandidateIds}
        loading={isLoadingCandidates}
        testMaxScore={getTestMaxScore(testConfig)}
      />

      {/* Modals (unchanged) */}
      {showJDModal && (
        <ModalBox title="View & Edit JD">
          <textarea
            value={jdContent}
            onChange={(e) => setJdContent(e.target.value)}
            rows={15}
            style={{ width: "90%", padding: "1rem" }}
          />
          <div className="modal-buttons">
            <button onClick={handleUpdateJD} disabled={isUpdatingJD}>
              {isUpdatingJD ? "Updating..." : "Update"}
            </button>
            <button className="cancel" onClick={() => setShowJDModal(false)}>Cancel</button>
          </div>
        </ModalBox>
      )}

      {showUploadJD && (
        <ModalBox title="Upload/Replace JD">
          <p>Please upload a `.docx` file only.</p>
          <input type="file" accept=".docx" onChange={(e) => setJdFile(e.target.files[0])} />
          <div className="modal-buttons">
            <button onClick={handleUploadJD}>Upload</button>
            <button className="cancel" onClick={() => setShowUploadJD(false)}>Cancel</button>
          </div>
        </ModalBox>
      )}

      {showUploadCV && (
        <ModalBox title="Upload CV List">
          <p>Please upload a `.xlsx` file only.</p>
          <input type="file" accept=".xlsx" onChange={(e) => setCvFile(e.target.files[0])} />
          <div className="modal-buttons">
            <button onClick={handleUploadCVs}>Upload</button>
            <button className="cancel" onClick={() => setShowUploadCV(false)}>Cancel</button>
          </div>
        </ModalBox>
      )}

      {showTestConfigModal && draftTestConfig && (
        <ModalBox
          title="Test Configuration"
          onClose={() => setShowTestConfigModal(false)}
          className="modal-wide"
        >
          <TestConfigForm
            value={draftTestConfig}
            durationMinutes={testDurationMinutes}
            onDurationChange={setTestDurationMinutes}
            onChange={setDraftTestConfig}
            onSave={() => {
              setTestConfig(draftTestConfig);
              handleSaveTestConfig(draftTestConfig);
            }}
            onCancel={() => {
              setDraftTestConfig(null);
              setShowTestConfigModal(false);
            }}
          />
        </ModalBox>
      )}

      {showUnfocusModal && unfocusData && (
        <ModalBox title="Un-focus activity">
          <p>
            <strong>Total un-focus count:</strong> {unfocusData.count}
          </p>

          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
              background: "#f8f9ff",
              padding: "10px",
              fontFamily: "monospace",
              fontSize: "13px",
              borderRadius: "6px",
            }}
          >
            {unfocusData.logs.length === 0 && (
              <i>No un-focus events recorded.</i>
            )}

            {unfocusData.logs.map((log, idx) => (
              <div key={idx}>
                [{log.at}] {log.event}
              </div>
            ))}
          </div>

          <div className="modal-buttons">
            <button onClick={() => setShowUnfocusModal(false)}>Close</button>
          </div>
        </ModalBox>
      )}
    </div>
  );
}

export default JobDetailPage;
